import { SimpleCarousel } from '@eventbrite/collection-carousel';
import { track } from '@eventbrite/datalayer-library';
import { FormattedEvent } from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { LoadingSkeleton } from '@eventbrite/loading-skeleton';
import { logEvent } from '@eventbrite/statsig';
import React from 'react';
import { connect } from 'react-redux';
import DiscoverVerticalEventCard from '../../../../components/DiscoverEventCard/DiscoverVerticalEventCard';
import {
    ConnectedProps,
    DiscoverState,
    LocationState,
} from '../../../../types/index';
import {
    HOMEPAGE_PAID_CAROUSEL_EVENT,
    HOMEPAGE_PAID_EVENTS_CAROUSEL_CTR_CLICK,
} from '../../constants/analytics';
import { useCategoryEventsQuery } from '../../hooks/events/events';
import { EventLoadingState } from '../SeasonalCarousel/SeasonalCarouselLoading';
import './PaidEventsCarousel.scss';

export enum NavDirections {
    Left = 'left',
    Right = 'right',
}
const renderLoadingSkeleton = (isMobile: boolean) => {
    return (
        <div className="loader">
            <LoadingSkeleton height={isMobile ? '400px' : '426px'} />
        </div>
    );
};

const renderEventLoadingState = () => {
    return (
        <div className="eventLoader">
            <EventLoadingState numberOfEvents={5} />
        </div>
    );
};

// Place ID mapped to a tuple consisting of place name and corresponding minPrice
const PLACE_ID_TO_NAME_AND_MIN_PRICE: {
    [key: string]: [string, number];
} = {
    101750367: ['London', 10],
    85936429: ['Atlanta', 20],
    85923517: ['Los Angeles', 20],
    101933229: ['Melbourne', 20],
    85933669: ['Miami', 20],
    85977539: ['New York', 30],
    85922583: ['San Francisco', 20],
    101735835: ['Toronto', 20],
    85931779: ['Washington DC', 20],
    101724577: ['Austin', 20],
    85949461: ['Baltimore', 20],
    85950361: ['Boston', 20],
    85981333: ['Charlotte', 30],
    101724385: ['Dallas', 30],
    85928879: ['Denver', 20],
    85951091: ['Detroit', 30],
    101751737: ['Dublin', 20],
    101725629: ['Houston', 20],
    85969169: ['Minneapolis', 20],
    85933091: ['Orlando', 20],
    101718083: ['Philadelphia', 30],
    85922227: ['San Diego', 30],
    85922347: ['San Jose', 20],
    101730401: ['Seattle', 30],
    101932003: ['Sydney', 20],
    85931789: ['Tampa', 20],
    101741075: ['Vancouver', 20],
};

// For locations with no placeID, similar mapping with the place name, parent and minPrice
const PLACE_NAME_TO_PARENT_AND_MIN_PRICE: { [key: string]: [string, number] } =
    {
        'Las Vegas': ['NV, USA', 10],
        Chicago: ['IL, USA', 20],
        Phoenix: ['AZ, USA', 30],
    };

const minPriceExperimentEnrolmentCheck = (location: LocationState) => {
    if (location.placeId)
        return location.placeId in PLACE_ID_TO_NAME_AND_MIN_PRICE;
    else
        return (
            location.currentPlace in PLACE_NAME_TO_PARENT_AND_MIN_PRICE &&
            PLACE_NAME_TO_PARENT_AND_MIN_PRICE[location.currentPlace][0] ===
                location.currentPlaceParent
        );
};

const getMinPrice = (placeId: string, currentPlace: string) => {
    return placeId == ''
        ? PLACE_NAME_TO_PARENT_AND_MIN_PRICE[currentPlace][1]
        : PLACE_ID_TO_NAME_AND_MIN_PRICE[placeId][1];
};

export const PaidEventsCarousel = ({
    isAuthenticated,
    shareOptions,
    locale,
    location,
    isMobile = false,
}: PaidEventsCarouselProps) => {
    const [pageSize, setPageSize] = React.useState(1);
    const [pageIndex, setPageIndex] = React.useState(1);
    const PAGE_SIZE = 40;
    const { data, isLoading } = useCategoryEventsQuery({
        queryOverload: {
            price: 'paid',
            page: 1,
            page_size: PAGE_SIZE,
            aggs: {},
            dates: ['current_future'],
        },
        refetchOnMount: false,
        location: location,
        enabled: true,
    });
    const moduleIndex = isMobile ? 40 : 10;
    React.useEffect(() => {
        if (location && location.currentPlace) {
            track({
                eventName: 'PaidEventsCarouselView',
                eventData: {
                    searchLocation: location.currentPlace,
                },
            });
        }
    }, [location]);

    if (!minPriceExperimentEnrolmentCheck(location)) return null;

    if (isLoading && pageIndex === 1) {
        return renderLoadingSkeleton(isMobile);
    } else if (isLoading) {
        return renderEventLoadingState();
    }

    let events: FormattedEvent[] = data?.events || [];

    const minPrice = getMinPrice(location.placeId || '', location.currentPlace);
    // minPrice object for an event has minPrice value as 1700 for a $17 ticket,
    // hence we need the division to get the actual value
    const MIN_PRICE_HUNDREDS_DIVISOR = 100;

    events = events.filter(
        (e) =>
            e.minPrice &&
            e.minPrice.minPriceValue / MIN_PRICE_HUNDREDS_DIVISOR <= minPrice,
    );

    if (events.length == 0) {
        return null;
    }

    // Since the events have a price filter, the page count may not be the same as
    // the one from original search response i.e. data?.pagination?.page_count
    const pageCount = Math.ceil(events.length / PAGE_SIZE);
    const numberFormatConfig = {
        style: 'currency',
        currencyDisplay: 'narrowSymbol',
        currency: events[0]?.minPrice?.currency,
    };

    // Returns the minPrice as per the locale's currency e.g. $20 for USA, £20 for London etc.
    const formattedMinPrice = new Intl.NumberFormat(
        locale.replace('_', '-'),
        numberFormatConfig,
    )
        .format(minPrice)
        .split('.')[0];

    const handleNavigationClick = (direction: string) => {
        if (direction === 'left' && (pageSize - 1) % moduleIndex == 0) {
            setPageSize(1);
            setPageIndex(pageIndex - 1);
        } else if (direction === 'right' && pageSize % moduleIndex == 0) {
            setPageSize(1);
            setPageIndex(pageIndex + 1);
        } else {
            direction === 'left'
                ? setPageSize(pageSize - 1)
                : setPageSize(pageSize + 1);
        }
        logEvent(HOMEPAGE_PAID_EVENTS_CAROUSEL_CTR_CLICK);
    };

    return (
        <div
            className="paid-events-carousel"
            data-heap-id="homepage-paid-events-carousel"
        >
            <section className="paid-events-carousel-container">
                <SimpleCarousel
                    headerTitle={
                        <div className="title-subtitle-container">
                            <h2>
                                {gettext(
                                    'Events %(formattedMinPrice)s and under',
                                    {
                                        formattedMinPrice,
                                    },
                                )}
                            </h2>
                        </div>
                    }
                    slidesVisible={{ desktop: 4, tablet: 3 }}
                    onNavigationClick={handleNavigationClick}
                    lazilyRenderOffScreenElements={true}
                    fullyEnableNavigation={true}
                    isFirstPage={pageIndex === 1}
                    isLastPage={pageIndex === pageCount}
                >
                    {events.map((event) => (
                        <div
                            className="paid-events-carousel-event-card"
                            data-heap-id="homepage-paid-events-carousel-click-event"
                            key={event.id}
                        >
                            <DiscoverVerticalEventCard
                                locale={locale || 'en_US'}
                                isLazyImage={true}
                                isAuthenticated={isAuthenticated || false}
                                event={event}
                                affCode="ehometext"
                                statsigLocationString={
                                    HOMEPAGE_PAID_CAROUSEL_EVENT
                                }
                                data-testid={`homepage-event-card-${event.id}`}
                                shareOptions={shareOptions}
                            />
                        </div>
                    ))}
                </SimpleCarousel>
            </section>
        </div>
    );
};

const _mapStateToProps = ({
    app,
    user,
    location,
    shareOptions,
}: DiscoverState) => {
    return {
        isMobile: app.isMobile,
        isAuthenticated: user.isAuthenticated,
        location: location,
        shareOptions: shareOptions,
        locale: app.locale,
    };
};

type PaidEventsCarouselProps = ConnectedProps<typeof _mapStateToProps>;
export default connect(_mapStateToProps)(PaidEventsCarousel);
